import {
  ReactElement, useCallback, useEffect, useState,
} from 'react';
import { HealthGraph } from './components/Graph';
import { useTranslation } from 'react-i18next';
import { GetUnitHealthHistory } from '../types';
import { useFilterContext } from '../contexts/FilterContext';
import { useToast } from 'contexts/newToastContext';
import { getUnitHealthHistory } from '../helpers/overview';
import { HistoryCardLoading } from '../components/Loading/History';
import { HistoryCard } from '../components/History';

interface HealthHistoryCardProps {
  hasHealth: boolean;
  loading: boolean;
}

export function HealthHistoryCard({
  hasHealth,
  loading,
}: Readonly<HealthHistoryCardProps>): ReactElement {
  const [graphData, setGraphData] = useState<GetUnitHealthHistory | undefined>();
  const [loadingGraph, setLoadingGraph] = useState(true);
  const { clients, selectedOptions } = useFilterContext();
  const { t } = useTranslation();
  const { toast } = useToast();

  const getHealthHistory = useCallback(async () => {
    setLoadingGraph(true);
    const clientIds = clients.map(Number);
    const data = await getUnitHealthHistory(clientIds, {
      stateIds: selectedOptions.state,
      unitIds: selectedOptions.units?.map((item) => Number(item.value)) ?? selectedOptions.group?.units,
      healthStatus: selectedOptions.healthStatus,
      cityIds: selectedOptions.cities?.map((item) => item.value),
    });

    if (data) {
      const orderData = [...data.MONTHLY_HISTORY].reverse();
      setGraphData({
        MONTHLY_HISTORY: orderData,
      });
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosHistoricoSaude'), 'error');
    }
    setLoadingGraph(false);
  }, [clients, selectedOptions, hasHealth, loading]);

  useEffect(() => {
    if (clients && clients.length > 0 && hasHealth && !loading) {
      getHealthHistory();
    }
  }, [clients, hasHealth, loading, getHealthHistory]);

  useEffect(() => {
    if (!hasHealth || loading) {
      setGraphData(undefined);
      setLoadingGraph(false);
    }
  }, [hasHealth, loading]);

  if (loading || (loadingGraph && hasHealth)) {
    return (
      <HistoryCardLoading />
    );
  }

  return (
    <HistoryCard
      id="health-history"
      dataTestId="health-history-card"
      description={t('mediaPorcentagemMesUnidadeStatusSaude')}
      title={t('historicoDeSaude')}
      disabledTooltip={!hasHealth}
    >
      <HealthGraph data={graphData} />
    </HistoryCard>
  );
}
